"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Loader, FileText, CircleCheck, CircleAlert } from "lucide-react";
const formSpec = {
  "form_key": "request_proposal_form_contact",
  "form_label": "Inquiry Form",
  "fields": [{
    "key": "name",
    "label": "Full Name",
    "type": "text",
    "required": true,
    "placeholder": "Evelyn Harper",
    "help_text": "Please enter your full name as you'd like it to appear.",
    "options": null
  }, {
    "key": "company",
    "label": "Partner's Name",
    "type": "text",
    "required": true,
    "placeholder": "James Sterling",
    "help_text": "We love to know both of your names.",
    "options": null
  }, {
    "key": "email",
    "label": "Email Address",
    "type": "email",
    "required": true,
    "placeholder": "hello@ivorylens.com",
    "help_text": "Where we should send your personalized brochure.",
    "options": null
  }, {
    "key": "phone",
    "label": "Wedding Date",
    "type": "text",
    "required": false,
    "placeholder": "June 12, 2025",
    "help_text": "Optional - helps us check our availability.",
    "options": null
  }, {
    "key": "services_needed",
    "label": "Desired Collections",
    "type": "checkbox",
    "required": true,
    "help_text": "Select the services that resonate with your vision.",
    "options": ["Full Day Wedding", "Engagement Session", "Elopement Coverage", "Fine Art Albums", "Film Photography"]
  }, {
    "key": "project_summary",
    "label": "Your Vision",
    "type": "textarea",
    "required": true,
    "placeholder": "Tell us about your vision, the atmosphere you're creating, and any specific moments you cherish.",
    "help_text": "The more you share, the better we can capture your unique story.",
    "options": null
  }]
};
function ProposalForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 px-6 text-center bg-accent/20 border border-border rounded-lg">
        <CircleCheck className="size-16 text-primary mb-6" />
        <h3 className="text-2xl font-bold font-default mb-2 text-foreground">Message Received</h3>
        <p className="text-muted-foreground max-w-md mb-8">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-default uppercase tracking-widest text-xs border-primary/20">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="bg-primary text-primary-foreground size-8 flex items-center justify-center rounded-md font-default text-sm font-bold">01</div>
          <h3 className="text-lg font-bold font-default uppercase tracking-widest text-foreground">Personal Details</h3>
          <Separator className="flex-1 bg-accent" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">Your Name *</FormLabel>
                <FormControl>
                  <Input placeholder={formSpec.fields[0].placeholder || ""} {...field} className="font-default border-0 border-b bg-transparent focus-visible:ring-0 focus-visible:border-primary transition-colors" />
                </FormControl>
                <FormDescription className="text-xs italic font-default">{formSpec.fields[0].help_text}</FormDescription>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="company" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">Partner's Name *</FormLabel>
                <FormControl>
                  <Input placeholder={formSpec.fields[1].placeholder || ""} {...field} className="font-default border-0 border-b bg-transparent focus-visible:ring-0 focus-visible:border-primary transition-colors" />
                </FormControl>
                <FormDescription className="text-xs italic font-default">{formSpec.fields[1].help_text}</FormDescription>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">Email Address *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={formSpec.fields[2].placeholder || ""} {...field} className="font-default border-0 border-b bg-transparent focus-visible:ring-0 focus-visible:border-primary transition-colors" />
                </FormControl>
                <FormDescription className="text-xs italic font-default">{formSpec.fields[2].help_text}</FormDescription>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">Wedding Date</FormLabel>
                <FormControl>
                  <Input type="text" placeholder={formSpec.fields[3].placeholder || ""} {...field} className="font-default border-0 border-b bg-transparent focus-visible:ring-0 focus-visible:border-primary transition-colors" />
                </FormControl>
                <FormDescription className="text-xs italic font-default">{formSpec.fields[3].help_text}</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-6 pt-4">
        <div className="flex items-center gap-4 mb-6">
          <div className="bg-primary text-primary-foreground size-8 flex items-center justify-center rounded-md font-default text-sm font-bold">02</div>
          <h3 className="text-lg font-bold font-default uppercase tracking-widest text-foreground">The Celebration</h3>
          <Separator className="flex-1 bg-accent" />
        </div>

        <FormField control={control} name="services_needed" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">Interested Collections *</FormLabel>
              <FormDescription className="text-xs mb-3 italic font-default">{formSpec.fields[4].help_text}</FormDescription>
              <FormControl>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {formSpec.fields[4].options?.map((option, i) => <label key={option} data-index={i} className={`flex items-start space-x-3 p-3 rounded-md border cursor-pointer transition-all duration-300 ${field.value?.includes(option) ? 'bg-accent/30 border-primary' : 'bg-transparent border-border hover:bg-accent/20'}`}>
                      <input type="checkbox" className="mt-1 size-4 rounded-sm border-primary text-primary focus:ring-ring" checked={field.value?.includes(option) || false} onChange={e => {
                const current = field.value || [];
                const updated = e.target.checked ? [...current, option] : current.filter((val: string) => val !== option);
                field.onChange(updated);
              }} />
                      <span className="text-sm font-default leading-none">
                        {option}
                      </span>
                    </label>)}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_summary" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">Tell us about your vision *</FormLabel>
              <FormControl>
                <textarea className="flex min-h-[160px] w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm shadow-sm transition-all duration-300 outline-none placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0 font-default resize-y" placeholder={formSpec.fields[5].placeholder || ""} {...field} />
              </FormControl>
              <FormDescription className="text-xs italic font-default">{formSpec.fields[5].help_text}</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-default">{submitError}</p>
        </div>}

      <div className="pt-6 flex justify-center">
        <Button type="submit" size="lg" disabled={isSubmitting} variant="outline" className="w-full md:w-auto min-w-[280px] font-default uppercase tracking-widest border-primary/40 hover:bg-primary hover:text-primary-foreground transition-all duration-500">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : <>
              <FileText className="mr-2 size-4" />
              Send Inquiry
            </>}
        </Button>
      </div>
    </Form>;
}
export default function RequestATechnicalProposal() {
  return <section data-section-id="224947" id="your-wedding-details" className="py-20 md:py-32 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
      backgroundImage: 'radial-gradient(var(--color-accent) 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }}>
      </div>
      
      <div className="container mx-auto px-4 md:px-6 max-w-4xl relative z-10">
        <div className="mb-12 md:mb-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded-lg bg-accent text-primary text-xs font-default font-medium uppercase tracking-widest mb-6">
            <span className="size-1.5 rounded-lg bg-primary animate-pulse"></span>
            Connect With Us
          </div>
          <h2 className="text-4xl md:text-6xl font-bold font-default tracking-tight text-foreground mb-6">
            Your Wedding Details
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default leading-relaxed">
            We are honored you’ve considered Ivory Lens to document your story. Please share the details of your celebration below, and we will be in touch to begin our journey together.
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl shadow-xl overflow-hidden">
          {}
          <div className="bg-secondary/30 border-b border-border px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <FileText className="size-4 text-muted-foreground" />
              <span className="font-default text-xs font-semibold text-foreground uppercase tracking-widest">Inquiry Form</span>
            </div>
            <div className="font-default text-[10px] text-muted-foreground uppercase tracking-widest">
              Est. Response: 24-48 Hours
            </div>
          </div>

          {}
          <div className="p-6 md:p-12">
            <FormProvider formKey="wedding_details_form_inquiry" formId="wedding_details_form_inquiry" formId={formSpec.form_key} sectionName="YourWeddingDetails" defaultValues={{
            name: "",
            company: "",
            email: "",
            phone: "",
            services_needed: [],
            project_summary: ""
          }} validationRules={{
            name: {
              required: "Your name is required"
            },
            company: {
              required: "Your partner's name is required"
            },
            email: {
              required: "Email address is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address format"
              }
            },
            services_needed: {
              required: "Please select at least one collection",
              custom: (val: string[]) => val && val.length > 0 ? null : "Please select at least one collection"
            },
            project_summary: {
              required: "Please share a bit about your vision",
              minLength: {
                value: 20,
                message: "We'd love to hear a little more about your plans (min 20 characters)"
              }
            }
          }} submitText="Send Inquiry" successMessage="Thank you for reaching out. Your details have been received with care. We will review your vision and contact you shortly to discuss your collection.">
              <ProposalForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}